import React, {
  KeyboardEventHandler,
  ChangeEventHandler,
  Component,
  RefObject,
  createRef,
} from 'react';
import classNames from 'classnames';
import styles from './input.styl';
import { uniqueId } from 'lodash';

export interface InputProps {
  id?: string;
  title?: string;
  placeholder?: string;
  type?: string;
  value?: string;
  hasError?: boolean;
  errorMessage?: string;
  showRequiredMark?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: (value: string) => void;
  disabled?: boolean;
  inputType?: 'small' | 'alternate';
  maxlength?: number;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
}

export class Input extends Component<InputProps> {
  private inputRef: RefObject<HTMLInputElement> = createRef();

  public focus = () => {
    if (!this.inputRef.current) {
      return;
    }

    this.inputRef.current.focus();
  };

  private onChange: ChangeEventHandler<HTMLInputElement> = event => {
    if (this.props.onChange) {
      this.props.onChange(event.target.value);
    }
  };

  private renderInput = (inputId: string) => (
    <input
      ref={this.inputRef}
      type={this.props.type || 'text'}
      id={inputId}
      name="first-name"
      className={classNames('text-input', styles.altInput, {
        error: this.props.hasError,
        'alt-input': this.props.inputType === 'alternate',
        'input-small': this.props.inputType === 'small',
        [styles.altInputEmpty]: !this.props.value,
      })}
      onBlur={this.props.onBlur}
      onFocus={this.props.onFocus}
      onChange={this.onChange}
      value={this.props.value}
      disabled={this.props.disabled}
      autoComplete="off"
      autoFocus={this.props.autoFocus}
      placeholder={this.props.inputType === 'alternate' ? undefined : this.props.placeholder}
      maxLength={this.props.maxlength}
      onKeyDown={this.props.onKeyDown}
    />
  );

  private renderLabel = (inputId: string) => {
    if (!this.props.title) {
      return null;
    }

    return (
      <label htmlFor={inputId}>
        {this.props.title} {this.props.showRequiredMark ? ' *' : ''}
      </label>
    );
  };

  render() {
    const { hasError, errorMessage, inputType } = this.props;

    const inputId = this.props.id ? this.props.id : uniqueId('input-id-');

    if (inputType === 'alternate') {
      return (
        <div className="input-group">
          {this.renderInput(inputId)}
          {this.renderLabel(inputId)}
          {hasError && errorMessage ? (
            <span className="mx-error-message">{errorMessage}</span>
          ) : null}
        </div>
      );
    }

    return (
      <div className={classNames(styles.inputGroupDefault)}>
        {this.renderLabel(inputId)}
        {this.renderInput(inputId)}
        {hasError && errorMessage ? <span className="mx-error-message">{errorMessage}</span> : null}
      </div>
    );
  }
}
